import React, { useState, useEffect, useRef } from 'react';
import { Icon } from '../../Icons';
import { PROJECTS } from '../../../utils/constants';
import { useRouting } from '../../../contexts/RoutingContext';
import type { Project } from '../../../types';
import styles from '../AppContent.module.css';
import localStyles from './Projects.module.css';

export const Projects: React.FC = () => {
  const { getInitialSubPath, setSubPath } = useRouting();
  const initializedRef = useRef(false);

  // Initialize from URL sub-path on mount
  const [selectedProject, setSelectedProject] = useState<Project | null>(() => {
    const subPathVal = getInitialSubPath('projects');
    if (subPathVal) {
      return PROJECTS.find(p => p.id === subPathVal) || null;
    }
    return null;
  });

  // Sync URL when selection changes (skip initial mount)
  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      return;
    }
    setSubPath('projects', selectedProject?.id || null);
  }, [selectedProject, setSubPath]);

  // Sort projects: featured first, then non-featured
  const sortedProjects = [...PROJECTS].sort((a, b) => {
    if (a.featured && !b.featured) return -1;
    if (!a.featured && b.featured) return 1;
    return 0;
  });

  if (selectedProject) {
    return (
      <div className={styles.appContainer}>
        <div className={styles.toolbar}>
          <button
            className={styles.toolbarButton}
            onClick={() => setSelectedProject(null)}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M10 2 L4 8 L10 14 L10 10 L14 10 L14 6 L10 6 Z"/>
            </svg>
            Back to Projects
          </button>
        </div>

        <div className={styles.contentPanel}>
          <div className={localStyles.projectDetail}>
            {/* Project Header */}
            <div className={localStyles.detailHeader}>
              <div className={localStyles.detailIcon}>
                <Icon name="folder-projects" size={48} />
              </div>
              <div className={localStyles.detailTitleSection}>
                <h1 className={localStyles.detailTitle}>{selectedProject.title}</h1>
                {(selectedProject.startDate || selectedProject.endDate) && (
                  <p className={localStyles.detailPeriod}>
                    {selectedProject.startDate} — {selectedProject.endDate || 'Present'}
                  </p>
                )}
              </div>
            </div>

            {/* Technologies */}
            <div className={localStyles.detailSection}>
              <h2 className={localStyles.detailSectionTitle}>
                <Icon name="settings" size={16} />
                Technologies
              </h2>
              <div className={styles.tags}>
                {selectedProject.technologies.map((tech) => (
                  <span key={tech} className={`${styles.tag} ${localStyles.techTag}`}>
                    {tech}
                  </span>
                ))}
              </div>
            </div>

            {/* Description */}
            <div className={localStyles.detailSection}>
              <h2 className={localStyles.detailSectionTitle}>
                <Icon name="document" size={16} />
                Overview
              </h2>
              <p className={localStyles.detailDescription}>
                {selectedProject.longDescription || selectedProject.description}
              </p>
            </div>

            {/* Features */}
            {selectedProject.features && selectedProject.features.length > 0 && (
              <div className={localStyles.detailSection}>
                <h2 className={localStyles.detailSectionTitle}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M6 10.5L3 7.5L1.5 9L6 13.5L14.5 5L13 3.5L6 10.5Z"/>
                  </svg>
                  Key Features
                </h2>
                <ul className={localStyles.featuresList}>
                  {selectedProject.features.map((feature, idx) => (
                    <li key={idx} className={localStyles.featureItem}>
                      <span className={localStyles.featureBullet}>✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Challenges */}
            {selectedProject.challenges && (
              <div className={localStyles.detailSection}>
                <h2 className={localStyles.detailSectionTitle}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M8 0L10 6L16 6L11 10L13 16L8 12L3 16L5 10L0 6L6 6Z"/>
                  </svg>
                  Technical Challenges
                </h2>
                <p className={localStyles.detailDescription}>
                  {selectedProject.challenges}
                </p>
              </div>
            )}

            {/* Links */}
            <div className={localStyles.detailSection}>
              <h2 className={localStyles.detailSectionTitle}>
                <Icon name="internet-explorer" size={16} />
                Links
              </h2>
              <div className={localStyles.detailLinks}>
                {selectedProject.liveUrl && (
                  <a
                    href={selectedProject.liveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={localStyles.detailLink}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M14 0H8L10 2L6 6L10 10L14 6L12 4H14V0Z"/>
                      <path d="M12 10V14H2V4H6L8 2H0V16H14V8L12 10Z"/>
                    </svg>
                    Live Demo
                  </a>
                )}
                {selectedProject.githubUrl && (
                  <a
                    href={selectedProject.githubUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={localStyles.detailLink}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"/>
                    </svg>
                    View Source Code
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.appContainer}>
      <div className={styles.contentPanel}>
        <div className={styles.section}>
          <h2 className={styles.sectionTitle} style={{ fontSize: 16, fontWeight: 'bold' }}>My Projects</h2>
          <p style={{ marginBottom: 16, fontSize: 13 }}>
            Click on a project to see detailed information.
          </p>

          <div className={`${styles.grid} ${styles.gridTwo}`}>
            {sortedProjects.map((project) => (
              <button
                key={project.id}
                className={`${styles.card} ${styles.cardHover} ${localStyles.projectCard}`}
                onClick={() => setSelectedProject(project)}
              >
                <h3 className={localStyles.projectTitle}>{project.title}</h3>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.statusBar}>
        {sortedProjects.length} projects
      </div>
    </div>
  );
};

export default Projects;
